import { useState, useEffect, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { getSpecification, createSpecification, updateSpecification, type Specification } from '../api/specification'
import { getDeviceContacts, createDeviceContact, updateDeviceContact, deleteDeviceContact } from '../api/connections'
import { createDeviceInstance } from '../api/deviceInstances'
import { getElectricalPanelById, getElectricalPanels } from '../api/electricalPanels'
// import { getSchemaTypes } from '../api/schemaTypes' // Legacy, не используется
import { getSchemas } from '../api/schemas'
import { getProject } from '../api/projects'
import type { DeviceContact } from '../types/connections'
import * as XLSX from 'xlsx'
import { useToast } from '../hooks/useToast'
import DeviceSpecEditModal from './modals/DeviceSpecEditModal'

interface DevicesProps {
  projectId: number
  electricalPanelId?: number
}

const DEVICE_TYPES: Array<{ value: string; label: string }> = [
  { value: 'lighting', label: 'Светильники' },
  { value: 'outlet', label: 'Розетки' },
  { value: 'switch', label: 'Выключатели' },
  { value: 'sensor', label: 'Датчики' },
  { value: 'actuator', label: 'Актуаторы' },
  { value: 'breaker', label: 'Автоматы' },
  { value: 'cable', label: 'Кабели' },
  { value: 'terminal', label: 'Клеммники' },
  { value: 'other', label: 'Прочие' }
]

const CONTACT_COLORS = [
  { value: '', label: 'Не указан', color: 'transparent' },
  { value: 'WH', label: 'WH - Белый', color: '#FFFFFF' },
  { value: 'BK', label: 'BK - Чёрный', color: '#000000' },
  { value: 'BU', label: 'BU - Синий', color: '#0066CC' },
  { value: 'BN', label: 'BN - Коричневый', color: '#8B4513' },
  { value: 'GY', label: 'GY - Серый', color: '#808080' },
  { value: 'GN', label: 'GN - Зеленый', color: '#008000' },
  { value: 'GNYE', label: 'GNYE - Жёлто-зелёный', color: '#FFD700', color2: '#008000' },
  { value: 'YE', label: 'YE - Желтый', color: '#FFD700' },
  { value: 'RD', label: 'RD - Красный', color: '#FF0000' },
  { value: 'OG', label: 'OG - Оранжевый', color: '#FFA500' },
  { value: 'PK', label: 'PK - Розовый', color: '#FFC0CB' },
  { value: 'VT', label: 'VT - Фиолетовый', color: '#8A2BE2' },
  // Цвета витой пары
  { value: 'WO', label: 'WO - Бело-оранжевый', color: '#FFFFFF', color2: '#FFA500' },
  { value: 'WG', label: 'WG - Бело-зелёный', color: '#FFFFFF', color2: '#008000' },
  { value: 'WB', label: 'WB - Бело-синий', color: '#FFFFFF', color2: '#0066CC' },
  { value: 'WN', label: 'WN - Бело-коричневый', color: '#FFFFFF', color2: '#8B4513' }
]

// Функция для получения иконки по типу схемы (legacy, для обратной совместимости)
// const getSchemaTypeIcon = (typeCode: string): string => {
//   switch (typeCode) {
//     case 'main': return '📊'
//     case 'breaker': return '⚡'
//     case 'knx': return '🔌'
//     case 'rs485': return '🔗'
//     case 'ethernet': return '🌐'
//     case 'terminal': return '🔧'
//     default: return '📋'
//   }
// }

const CONTACT_POSITIONS = [
  { value: 'top', label: 'Верхний', icon: '⬆️' },
  { value: 'bottom', label: 'Нижний', icon: '⬇️' },
  { value: 'left', label: 'Левый', icon: '⬅️' },
  { value: 'right', label: 'Правый', icon: '➡️' }
]



export default function Devices({ projectId, electricalPanelId }: DevicesProps) {
  const params = useParams<{ projectId?: string; panelId?: string }>()
  const currentProjectId = params.projectId ? Number(params.projectId) : projectId
  const currentPanelId = params.panelId ? Number(params.panelId) : electricalPanelId
  const toast = useToast()
  
  const [selectedDevice, setSelectedDevice] = useState<Specification | null>(null)
  const [contacts, setContacts] = useState<DeviceContact[]>([])
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [showEditForm, setShowEditForm] = useState(false)
  const [showContactManager, setShowContactManager] = useState(false)
  const [showContactEditor, setShowContactEditor] = useState(false)
  const [editingContact, setEditingContact] = useState<DeviceContact | null>(null)
  const [copyDevice, setCopyDevice] = useState<Specification | null>(null)
  const [mouseDownTarget, setMouseDownTarget] = useState<EventTarget | null>(null)

  // Terminal-specific modals
  const [showTerminalCreateForm, setShowTerminalCreateForm] = useState(false)
  const [showTerminalEditForm, setShowTerminalEditForm] = useState(false)
  const [editingTerminal, setEditingTerminal] = useState<Specification | null>(null)

  // Device instance creation modal
  const [showCreateInstanceForm, setShowCreateInstanceForm] = useState(false)
  const [selectedDeviceForInstance, setSelectedDeviceForInstance] = useState<Specification | null>(null)

  const [newContact, setNewContact] = useState<{
    contact_number: number | string
    contact_label: string
    color_code: string
    contact_position: 'top' | 'bottom' | 'left' | 'right'
    selected_schema_ids: number[] // Массив ID конкретных схем щита
  }>({
    contact_number: 1,
    contact_label: 'Li',
    color_code: '',
    contact_position: 'top',
    selected_schema_ids: [] // По умолчанию пусто
  })
  const [filterType, setFilterType] = useState<string>('')

  // Sorting state
  const [sortField, setSortField] = useState<keyof Specification | 'power_consumption' | 'length_meters' | 'instance_count'>('id')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  const queryClient = useQueryClient()

  // Load project info
  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => getProject(projectId),
    enabled: !!projectId
  })

  // Load electrical panel info
  const { data: electricalPanel, isLoading: electricalPanelLoading } = useQuery({
    queryKey: ['electrical-panel', currentPanelId],
    queryFn: () => getElectricalPanelById(currentPanelId!),
    enabled: !!currentPanelId
  })

  // Load schema types for contact type selection (legacy, для обратной совместимости)
  // const { data: schemaTypes = [], isLoading: schemaTypesLoading } = useQuery({
  //   queryKey: ['schema-types'],
  //   queryFn: getSchemaTypes
  // })

  // Load all panels for the project
  const { data: allPanels = [] } = useQuery({
    queryKey: ['electrical-panels', currentProjectId],
    queryFn: () => getElectricalPanels(currentProjectId),
    enabled: !!currentProjectId
  })

  // Load schemas for all panels in the project
  const { data: allSchemas = [], isLoading: panelSchemasLoading } = useQuery({
    queryKey: ['schemas-all-panels', currentProjectId],
    queryFn: async () => {
      if (!currentProjectId || allPanels.length === 0) return []
      
      // Загружаем схемы для всех щитов проекта
      const schemasPromises = allPanels.map(panel => 
        getSchemas(currentProjectId, panel.id).catch(err => {
          console.error(`Failed to load schemas for panel ${panel.id}:`, err)
          return []
        })
      )
      
      const schemasArrays = await Promise.all(schemasPromises)
      // Объединяем все схемы в один массив
      return schemasArrays.flat()
    },
    enabled: !!currentProjectId && allPanels.length > 0
  })

  // Create device instance mutation
  const createDeviceInstanceMutation = useMutation({
    mutationFn: ({ projectId, specificationId, electricalPanelId, positionLabel, description }: {
      projectId: number; 
      specificationId: number; 
      electricalPanelId: number;
      positionLabel: string;
      description?: string;
    }) => createDeviceInstance({
      specification_id: specificationId,
      project_id: projectId,
      electrical_panel_id: electricalPanelId,
      position_label: positionLabel,
      description: description
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['specification', currentProjectId] })
      setShowCreateInstanceForm(false)
      setSelectedDeviceForInstance(null)
      toast.success('Экземпляр устройства успешно создан')
    },
    onError: (error) => {
      console.error('❌ Failed to create device instance:', error)
      alert(`Ошибка создания экземпляра: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`)
    }
  })

  // Sorting functions
  const handleSort = (field: keyof Specification | 'power_consumption' | 'length_meters' | 'instance_count') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  // Excel export function
  const exportToExcel = () => {
    if (!devices || devices.length === 0) {
      alert('Нет данных для экспорта')
      return
    }

    // Prepare data for Excel
    const excelData = devices.map(device => ({
      'ID': device.id,
      'Название': device.name,
      'Модель': device.model || '',
      'Производитель': device.manufacturer || '',
      'Тип устройства': device.device_type_name || '',
      'Количество экземпляров': device.instance_count || 0,
      'Цена за единицу': device.unit_price ? `${device.unit_price} руб.` : '',
      'Длина (м)': device.length_meters || '',
      'Свойства': device.properties ? JSON.stringify(device.properties) : '',
      'Дата создания': device.created_at ? new Date(device.created_at).toLocaleDateString('ru-RU') : ''
    }))

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.json_to_sheet(excelData)

    // Set column widths
    const colWidths = [
      { wch: 8 },   // ID
      { wch: 30 },  // Название
      { wch: 20 },  // Модель
      { wch: 20 },  // Производитель
      { wch: 20 },  // Тип устройства
      { wch: 15 },  // Количество экземпляров
      { wch: 15 },  // Цена за единицу
      { wch: 12 },  // Длина
      { wch: 30 },  // Свойства
      { wch: 15 }   // Дата создания
    ]
    ws['!cols'] = colWidths

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Спецификации устройств')

    // Generate filename
    const panelName = electricalPanel?.name || electricalPanel?.proj_id || 'Щит'
    const projectName = project?.name || 'Проект'
    const dateStr = new Date().toISOString().split('T')[0]
    const filename = `Спецификация_устройств_${projectName}_${panelName}_${dateStr}.xlsx`

    // Save file
    XLSX.writeFile(wb, filename)
  }

  // Sortable header component
  const SortableHeader = ({ field, children }: { field: keyof Specification | 'power_consumption' | 'length_meters' | 'instance_count', children: React.ReactNode }) => (
    <th 
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center space-x-1">
        <span>{children}</span>
        <span className="flex flex-col">
          <span className={`text-xs ${sortField === field && sortDirection === 'asc' ? 'text-blue-600' : 'text-gray-300'}`}>▲</span>
          <span className={`text-xs ${sortField === field && sortDirection === 'desc' ? 'text-blue-600' : 'text-gray-300'}`}>▼</span>
        </span>
      </div>
    </th>
  )

  // Function to close all modals
  const closeAllModals = () => {
    setShowCreateForm(false)
    setShowEditForm(false)
    setShowContactManager(false)
    setShowContactEditor(false)
    setShowTerminalCreateForm(false)
    setShowTerminalEditForm(false)
    setShowCreateInstanceForm(false)
    setSelectedDevice(null)
    setSelectedDeviceForInstance(null)
    setEditingTerminal(null)
    setEditingContact(null)
    setCopyDevice(null)
    setMouseDownTarget(null)
  }

  // Function to close only contact editor (keep contact manager open)
  const closeContactEditor = () => {
    setShowContactEditor(false)
    setEditingContact(null)
    setNewContact({
      contact_number: 1,
      contact_label: 'Li',
      color_code: '',
      contact_position: 'top',
      selected_schema_ids: [] // Начинаем с пустого массива
    })
  }


  // Handle submit device instance creation
  const handleSubmitDeviceInstance = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    
    if (!selectedDeviceForInstance || !currentPanelId) {
      toast.error('Ошибка: не выбрано устройство или электрический щит')
      return
    }
    
    const formData = new FormData(e.currentTarget)
    const positionLabel = formData.get('position_label') as string
    const description = formData.get('description') as string
    
    if (!positionLabel.trim()) {
      toast.error('Пожалуйста, введите позиционное обозначение')
      return
    }
    
    const instanceData = {
      projectId: currentProjectId,
      specificationId: selectedDeviceForInstance.id,
      electricalPanelId: currentPanelId,
      positionLabel: positionLabel.trim(),
      description: description.trim() || undefined
    }

    createDeviceInstanceMutation.mutate(instanceData)
  }

  // Handle ESC key to close modals
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (showContactEditor) {
          closeContactEditor()
        } else {
          closeAllModals()
        }
      }
    }

    // Add event listener when any modal is open
    if (showCreateForm || showEditForm || showContactEditor || showContactManager || 
        showTerminalCreateForm || showTerminalEditForm || showCreateInstanceForm) {
      document.addEventListener('keydown', handleEscKey)
    }

    // Cleanup function
    return () => {
      document.removeEventListener('keydown', handleEscKey)
    }
  }, [showCreateForm, showEditForm, showContactEditor, showContactManager, 
      showTerminalCreateForm, showTerminalEditForm, showCreateInstanceForm])



  // Load device contacts when device is selected
  useEffect(() => {
    if (selectedDevice) {
      loadDeviceContacts(selectedDevice.id)
    }
  }, [selectedDevice])

  const loadDeviceContacts = async (specificationId: number) => {
    try {
      const deviceContacts = await getDeviceContacts(specificationId)
      // Сортируем контакты по номеру (числовая сортировка)
      const sortedContacts = deviceContacts.sort((a, b) => {
        const numA = parseInt(String(a.contact_number))
        const numB = parseInt(String(b.contact_number))
        return numA - numB
      })
      setContacts(sortedContacts)
    } catch (error) {
      console.error('Failed to load device contacts:', error)
      setContacts([])
    }
  }

  // Load device specifications for the project
  const { data: devices = [], isLoading: devicesLoading, error: devicesError } = useQuery({
    queryKey: ['specification', currentProjectId, filterType, currentPanelId],
    queryFn: () => getSpecification(currentProjectId, filterType || undefined, currentPanelId),
    enabled: !!currentProjectId
  })

  // Sorted devices
  const sortedDevices = useMemo(() => {
    return [...devices].sort((a, b) => {
      let aValue: any
      let bValue: any

      if (sortField === 'power_consumption') {
        aValue = a.power_consumption || 0
        bValue = b.power_consumption || 0
      } else if (sortField === 'length_meters') {
        aValue = a.length_meters || 0
        bValue = b.length_meters || 0
      } else if (sortField === 'instance_count') {
        aValue = a.instance_count || 0
        bValue = b.instance_count || 0
      } else {
        aValue = a[sortField]
        bValue = b[sortField]
      }

      // Handle different data types
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase()
        bValue = bValue.toLowerCase()
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
      return 0
    })
  }, [devices, sortField, sortDirection])

  // Create device mutation
  const createDeviceMutation = useMutation({
    mutationFn: (data: Omit<Specification, 'id' | 'created_at'>) => {
      const dataWithPanel = {
        ...data,
        project_id: currentProjectId,
        ...(currentPanelId && { electrical_panel_id: currentPanelId })
      }
      return createSpecification(dataWithPanel)
    },
    onSuccess: (createdSpecification) => {
      queryClient.invalidateQueries({ queryKey: ['specification', currentProjectId, filterType, currentPanelId] })
      queryClient.invalidateQueries({ queryKey: ['specification', currentProjectId] })
      toast.success('Устройство успешно создано')
      
      // Закрываем форму создания, но открываем форму создания экземпляра
      setShowCreateForm(false)
      setCopyDevice(null)
      
      // Автоматически открываем окно создания экземпляра с созданной спецификацией
      if (currentPanelId && createdSpecification) {
        setSelectedDeviceForInstance(createdSpecification)
        setShowCreateInstanceForm(true)
      }
    }
  })

  // Update device mutation


  // Terminal mutations
  const createTerminalMutation = useMutation({
    mutationFn: (data: Omit<Specification, 'id' | 'created_at'>) => {
      const dataWithPanel = {
        ...data,
        project_id: currentProjectId,
        ...(currentPanelId && { electrical_panel_id: currentPanelId })
      }
      return createSpecification(dataWithPanel)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['specification', currentProjectId] })
      closeAllModals()
    },
    onError: (error) => {
      console.error('Error creating terminal:', error)
    }
  })

  const updateTerminalMutation = useMutation({
    mutationFn: async (data: { id: number; updates: Partial<Specification> }) => {
      await updateSpecification(data.id, data.updates)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['specification', currentProjectId] })
      closeAllModals()
      toast.success('Клеммник успешно обновлен')
    }
  })

  // Contact mutations
  const createContactMutation = useMutation({
    mutationFn: createDeviceContact
  })

  const updateContactMutation = useMutation({
    mutationFn: async (data: { id: number; updates: any }) => {
      await updateDeviceContact(data.id, data.updates)
    }
  })

  const deleteContactMutation = useMutation({
    mutationFn: deleteDeviceContact,
    onSuccess: () => {
      if (selectedDevice) {
        loadDeviceContacts(selectedDevice.id)
      }
    }
  })

  // Function to automatically create terminal contacts based on level count
const createTerminalContacts = async (specificationId: number, levelCount: number) => {
  const contacts = [];
  
  // Create contacts for each level (U = Upper, D = Lower)
  for (let level = 1; level <= levelCount; level++) {
    // Upper contact
    contacts.push({
      specification_id: specificationId,
      contact_number: `${level}U`,
      contact_label: `${level}U`,
      color_code: ''
    });
    
    // Lower contact
    contacts.push({
      specification_id: specificationId,
      contact_number: `${level}D`,
      contact_label: `${level}D`,
      color_code: ''
    });
  }
  
  // Create contacts in parallel
  await Promise.all(contacts.map(contact => 
    createContactMutation.mutateAsync(contact)
  ));
  
  // Create internal connections between corresponding levels
  try {
    const { createTerminalInternalConnections } = await import('../api/terminalBlockAssignments');
    await createTerminalInternalConnections(specificationId, levelCount);
  } catch (error) {
    console.error('Error creating internal connections:', error);
  }
};

  const handleCreateDevice = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    
    // Get device_type directly from form (it's already a string code)
    const deviceTypeCode = formData.get('device_type') as string || 'lighting'
    
    // Parse dimensions and convert to mm * 10
    const widthValue = formData.get('width') as string
    const heightValue = formData.get('height') as string
    const depthValue = formData.get('depth') as string
    
    const deviceData: Omit<Specification, 'id' | 'created_at'> = {
      project_id: currentProjectId,
      device_type: deviceTypeCode, // Use code directly from form
      name: formData.get('name') as string,
      model: formData.get('model') as string || undefined,
      manufacturer: formData.get('manufacturer') as string || undefined,
      power_consumption: parseFloat(formData.get('power_consumption') as string) || undefined,
      length_meters: parseFloat(formData.get('length_meters') as string) || undefined,
      visual_template: formData.get('visual_template') as string || undefined,
      documentation_url: formData.get('documentation_url') as string || undefined,
      width: widthValue ? Math.round(parseFloat(widthValue) * 10) : undefined,
      height: heightValue ? Math.round(parseFloat(heightValue) * 10) : undefined,
      depth: depthValue ? Math.round(parseFloat(depthValue) * 10) : undefined
    }



    createDeviceMutation.mutate(deviceData)
  }


  // Terminal form handlers
  const handleCreateTerminal = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    
    const levelCount = parseInt(formData.get('contact_count') as string) || 1
    
    const terminalData: Omit<Specification, 'id' | 'created_at'> = {
      project_id: projectId,
      device_type_id: 8, // ID для клеммников в device_types
      name: formData.get('name') as string,
      model: formData.get('model') as string || undefined,
      manufacturer: formData.get('manufacturer') as string || undefined,
      power_consumption: parseFloat(formData.get('power_consumption') as string) || undefined
    }

    try {
      const result = await createTerminalMutation.mutateAsync(terminalData)
      
      // Automatically create contacts for the terminal
      if (result?.id) {
        await createTerminalContacts(result.id, levelCount)
      }
      
      setShowTerminalCreateForm(false)
    } catch (error) {
      console.error('Error creating terminal:', error)
    }
  }

  const handleEditTerminal = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!editingTerminal) return
    
    const formData = new FormData(e.currentTarget)
    
    // Parse dimensions and convert to mm * 10
    const widthValue = formData.get('width') as string
    const heightValue = formData.get('height') as string
    const depthValue = formData.get('depth') as string
    
    const updates: Partial<Specification> = {
      name: formData.get('name') as string,
      model: formData.get('model') as string || undefined,
      manufacturer: formData.get('manufacturer') as string || undefined,
      power_consumption: parseFloat(formData.get('power_consumption') as string) || undefined,
      width: widthValue ? Math.round(parseFloat(widthValue) * 10) : undefined,
      height: heightValue ? Math.round(parseFloat(heightValue) * 10) : undefined,
      depth: depthValue ? Math.round(parseFloat(depthValue) * 10) : undefined
    }

    updateTerminalMutation.mutate({ id: editingTerminal.id, updates })
  }


  const handleCreateContact = async () => {
    if (!selectedDevice) return

    try {
      // Отправляем schema_ids (массив ID конкретных схем)
      const schema_ids = newContact.selected_schema_ids || [];

      if (editingContact) {
        // Update existing contact
        const updates = {
          contact_number: newContact.contact_number,
          contact_label: newContact.contact_label,
          color_code: newContact.color_code,
          contact_position: newContact.contact_position,
          schema_ids: schema_ids // Новое поле для конкретных схем
        }
        
        await updateContactMutation.mutateAsync({ id: editingContact.id, updates })
        
      } else {
        // Create new contact
        const contactData = {
          specification_id: selectedDevice.id,
          contact_number: newContact.contact_number,
          contact_label: newContact.contact_label,
          color_code: newContact.color_code,
          contact_position: newContact.contact_position,
          schema_ids: schema_ids // Новое поле для конкретных схем
        }
        
        await createContactMutation.mutateAsync(contactData)
      }
      
      // If we get here, operation was successful

      
    } catch (error) {
      console.error('Error saving contact:', error)
      // Even if there's an error, we'll still try to refresh and close
    }
    
    // Always reload contacts and close editor
    if (selectedDevice) {
      loadDeviceContacts(selectedDevice.id)
    }
    closeContactEditor()
  }

  const handleEditContact = (contact: DeviceContact) => {
    setEditingContact(contact)
    
    // Convert schema_ids (или schema_type_ids для обратной совместимости) в selected_schema_ids
    let selected_schema_ids: number[] = [];
    
    // Сначала проверяем новое поле schema_ids
    if ((contact as any).schema_ids) {
      try {
        if (Array.isArray((contact as any).schema_ids)) {
          selected_schema_ids = (contact as any).schema_ids;
        } else if (typeof (contact as any).schema_ids === 'string') {
          selected_schema_ids = JSON.parse((contact as any).schema_ids);
        }
      } catch (error) {
        console.warn('Error parsing schema_ids:', error);
      }
    }
    
    // Fallback: если schema_ids нет, пытаемся получить схемы по schema_type_ids
    if (selected_schema_ids.length === 0 && contact.schema_type_ids && allSchemas.length > 0) {
      try {
        let schemaTypeIds: number[] = [];
        if (Array.isArray(contact.schema_type_ids)) {
          schemaTypeIds = contact.schema_type_ids;
        } else if (typeof contact.schema_type_ids === 'string') {
          schemaTypeIds = JSON.parse(contact.schema_type_ids);
        }
        
        // Находим все схемы с подходящими типами
        selected_schema_ids = allSchemas
          .filter(schema => schemaTypeIds.includes(schema.schema_type_id || 0))
          .map(schema => schema.id);
      } catch (error) {
        console.warn('Error parsing schema_type_ids:', error);
      }
    }
    
    setNewContact({
      contact_number: contact.contact_number,
      contact_label: contact.contact_label || '',
      color_code: contact.color_code || '',
      contact_position: contact.contact_position || 'top',
      selected_schema_ids: selected_schema_ids
    })
    setShowContactEditor(true)
  }

  const handleDeleteContact = (contact: DeviceContact) => {
    if (confirm(`Удалить контакт "${contact.contact_label}"?`)) {
      deleteContactMutation.mutate(contact.id)
    }
  }

  const handleOpenNewContactEditor = () => {
    // Calculate next contact number automatically
    let nextContactNumber = 1
    if (contacts.length > 0) {
      const existingNumbers = contacts.map(contact => {
        const contactNum = contact.contact_number.toString()
        // Extract numeric part from contact numbers like "1-TOP", "2-BOTTOM", etc.
        const match = contactNum.match(/^(\d+)/)
        return match ? parseInt(match[1]) : 0
      })
      const maxNumber = Math.max(...existingNumbers)
      nextContactNumber = maxNumber + 1
    }

    // Reset editing state and set calculated contact number
    setEditingContact(null)
    setNewContact({
      contact_number: nextContactNumber,
      contact_label: 'Li',
      color_code: '',

      contact_position: 'top',
      selected_schema_ids: [] // Начинаем с пустого массива
    })
    setShowContactEditor(true)
  }

  const handleQuickAddLN = async () => {
    if (!selectedDevice) return

    try {
      // Get next contact numbers
      let nextContactNumber = 1
      if (contacts.length > 0) {
        const existingNumbers = contacts.map(contact => {
          const contactNum = contact.contact_number.toString()
          const match = contactNum.match(/^(\d+)/)
          return match ? parseInt(match[1]) : 0
        })
        const maxNumber = Math.max(...existingNumbers)
        nextContactNumber = maxNumber + 1
      }

      // Create L contact
      await createContactMutation.mutateAsync({
        specification_id: selectedDevice.id,
        contact_number: nextContactNumber,
        contact_label: 'L',
        color_code: 'BN'
      })

      // Create N contact
      await createContactMutation.mutateAsync({
        specification_id: selectedDevice.id,
        contact_number: nextContactNumber + 1,
        contact_label: 'N',
        color_code: 'BU'
      })

      // Reload contacts and close editor
      loadDeviceContacts(selectedDevice.id)
      closeContactEditor()
      
    } catch (error) {
      console.error('Error creating L, N contacts:', error)
    }
  }

  const handleQuickAddLNPE = async () => {
    if (!selectedDevice) return

    try {
      // Get next contact numbers
      let nextContactNumber = 1
      if (contacts.length > 0) {
        const existingNumbers = contacts.map(contact => {
          const contactNum = contact.contact_number.toString()
          const match = contactNum.match(/^(\d+)/)
          return match ? parseInt(match[1]) : 0
        })
        const maxNumber = Math.max(...existingNumbers)
        nextContactNumber = maxNumber + 1
      }

      // Create L contact
      await createContactMutation.mutateAsync({
        specification_id: selectedDevice.id,
        contact_number: nextContactNumber,
        contact_label: 'L',
        color_code: 'BN'
      })

      // Create N contact
      await createContactMutation.mutateAsync({
        specification_id: selectedDevice.id,
        contact_number: nextContactNumber + 1,
        contact_label: 'N',
        color_code: 'BU'
      })

      // Create PE contact
      await createContactMutation.mutateAsync({
        specification_id: selectedDevice.id,
        contact_number: nextContactNumber + 2,
        contact_label: 'PE',
        color_code: 'GN'
      })

      // Reload contacts and close editor
      loadDeviceContacts(selectedDevice.id)
      closeContactEditor()
      
    } catch (error) {
      console.error('Error creating L, N, PE contacts:', error)
    }
  }

  const handleQuickAdd12 = async () => {
    if (!selectedDevice) return

    try {
      // Get next contact numbers
      let nextContactNumber = 1
      if (contacts.length > 0) {
        const existingNumbers = contacts.map(contact => {
          const contactNum = contact.contact_number.toString()
          const match = contactNum.match(/^(\d+)/)
          return match ? parseInt(match[1]) : 0
        })
        const maxNumber = Math.max(...existingNumbers)
        nextContactNumber = maxNumber + 1
      }

      // Create contact "1"
      await createContactMutation.mutateAsync({
        specification_id: selectedDevice.id,
        contact_number: nextContactNumber,
        contact_label: '1',
        color_code: ''
      })

      // Create contact "2"
      await createContactMutation.mutateAsync({
        specification_id: selectedDevice.id,
        contact_number: nextContactNumber + 1,
        contact_label: '2',
        color_code: ''
      })

      // Reload contacts and close editor
      loadDeviceContacts(selectedDevice.id)
      closeContactEditor()
      
    } catch (error) {
      console.error('Error creating 1, 2 contacts:', error)
    }
  }



  if (devicesError) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Device Specifications</h2>
        <div className="text-red-600 bg-red-50 p-4 rounded-md">
          Ошибка загрузки спецификаций устройств: {devicesError instanceof Error ? devicesError.message : 'Неизвестная ошибка'}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Devices Section */}
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="mb-4">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Экземпляры спецификации устройств</h2>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm w-full sm:w-auto"
          >
              <option value="">Все типы</option>
            {DEVICE_TYPES.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={exportToExcel}
              className="bg-orange-600 text-white px-3 py-2 rounded-lg hover:bg-orange-700 flex items-center gap-2 text-sm whitespace-nowrap"
              title="Экспорт спецификации в Excel"
            >
                📊 Excel
            </button>
            <Link
              to={`/projects/${currentProjectId}/specification`}
              className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 text-sm whitespace-nowrap"
            >
              Вся спецификация проекта
            </Link>
          </div>
        </div>
      </div>

      {/* Loading state */}
      {devicesLoading && (
        <div className="text-center py-8 text-gray-500">Загрузка спецификаций устройств...</div>
      )}

      {/* Devices table - Desktop */}
      {!devicesLoading && devices.length > 0 && (
        <>
          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <SortableHeader field="id">ID</SortableHeader>
                  <SortableHeader field="manufacturer">Производитель</SortableHeader>
                  <SortableHeader field="model">Модель</SortableHeader>
                  <SortableHeader field="name">Название</SortableHeader>
                  <SortableHeader field="instance_count">Количество экземпляров</SortableHeader>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Действия
                    </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedDevices.map((device: Specification) => (
                  <tr 
                    key={device.id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      #{device.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {device.manufacturer || 'Не указан'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {device.model || 'Не указана'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link
                        to={currentPanelId 
                          ? `/projects/${currentProjectId}/panels/${currentPanelId}/devices/${device.id}`
                          : `/projects/${currentProjectId}/devices/${device.id}`
                        }
                        className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {device.name}
                      </Link>
                      <div className="text-sm text-gray-500">
                        {DEVICE_TYPES.find(t => t.value === device.device_type_code)?.label || device.device_type_name || 'Неизвестный тип'}
                        {device.power_consumption && ` • ${device.power_consumption}Вт`}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <Link
                        to={`/projects/${currentProjectId}/device-instances?spec=${device.id}`}
                        className="text-blue-600 hover:text-blue-800 hover:underline"
                        title="Просмотр экземпляров устройства"
                      >
                        {device.instance_count || 0}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => {
                          setSelectedDevice(device)
                          setShowEditForm(true)
                        }}
                        className="inline-flex items-center text-blue-600 hover:text-blue-800 hover:underline"
                        title="Изменить спецификацию устройства"
                      >
                        ✏️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-4">
            {sortedDevices.map((device: Specification) => (
              <div
                key={device.id}
                className="border rounded-lg p-4 border-gray-200 hover:border-gray-300 transition-colors"
              >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 text-sm">
                      <Link
                        to={currentPanelId 
                          ? `/projects/${currentProjectId}/panels/${currentPanelId}/devices/${device.id}`
                          : `/projects/${currentProjectId}/devices/${device.id}`
                        }
                        className="text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {device.name}
                      </Link>
                      {' '}
                      <span className="text-gray-400">#{device.id}</span>
                  </h3>
                    <div className="text-xs text-gray-600 mt-1">
                      <div>{device.manufacturer || 'Производитель не указан'}</div>
                      <div>{device.model || 'Модель не указана'}</div>
                      <div>{DEVICE_TYPES.find(t => t.value === device.device_type_code)?.label || device.device_type_name || 'Неизвестный тип'}</div>
                      <div className="font-medium">
                        Количество экземпляров:{' '}
                        <Link
                          to={`/projects/${currentProjectId}/device-instances?spec=${device.id}`}
                          className="text-blue-600 hover:text-blue-800 hover:underline"
                          title="Просмотр экземпляров устройства"
                        >
                          {device.instance_count || 0}
                        </Link>
                      </div>
                  </div>
                </div>
                  <div className="ml-2">
                    <button
                      onClick={() => {
                        setSelectedDevice(device)
                        setShowEditForm(true)
                      }}
                      className="inline-flex items-center text-blue-600 hover:text-blue-800 hover:underline text-sm"
                      title="Изменить спецификацию устройства"
                    >
                      ✏️
                    </button>
                  </div>
                </div>
              </div>
          ))}
        </div>
        </>
      )}

      {/* Empty state */}
      {!devicesLoading && devices.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <p className="mb-4">Спецификации устройств для этого проекта не найдены{filterType && ` (отфильтровано по ${DEVICE_TYPES.find(t => t.value === filterType)?.label})`}.</p>
          <Link
            to={`/projects/${currentProjectId}/specification`}
            className="inline-block bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Перейти к спецификации проекта
          </Link>
        </div>
      )}

      {/* Create Form Modal */}
      {showCreateForm && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setMouseDownTarget(e.target)
            }
          }}
          onMouseUp={(e) => {
            if (e.target === e.currentTarget && mouseDownTarget === e.target) {
              closeAllModals()
            }
            setMouseDownTarget(null)
          }}
        >
          <div 
            className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-4">
              {copyDevice ? `Копировать "${copyDevice.name}"` : 'Создать спецификацию устройства'}
            </h3>
            <form onSubmit={handleCreateDevice} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Тип устройства</label>
                  <select
                    name="device_type"
                    required
                    defaultValue={copyDevice?.device_type_code || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="">Выберите тип...</option>
                    {DEVICE_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Название</label>
                  <input
                    type="text"
                    name="name"
                    required
                    defaultValue={copyDevice ? `${copyDevice.name} (копия)` : ''}
                    placeholder="напр., QF 16A, Розетка 220В"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Модель</label>
                  <input
                    type="text"
                    name="model"
                    defaultValue={copyDevice?.model || ''}
                    placeholder="напр., C16 2P"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Производитель</label>
                  <input
                    type="text"
                    name="manufacturer"
                    defaultValue={copyDevice?.manufacturer || ''}
                    placeholder="напр., ABB, Schneider"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>



                <div>
                  <label className="block text-sm font-medium mb-1">Потребляемая мощность (Вт)</label>
                  <input
                    type="number"
                    name="power_consumption"
                    step="0.1"
                    min="0"
                    defaultValue={copyDevice?.power_consumption || ''}
                    placeholder="напр., 15.5"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Длина (метры)</label>
                  <input
                    type="number"
                    name="length_meters"
                    step="0.1"
                    min="0"
                    defaultValue={copyDevice?.length_meters || ''}
                    placeholder="Только для кабелей"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Ширина (мм)</label>
                  <input
                    type="number"
                    name="width"
                    step="0.1"
                    min="0"
                    defaultValue={copyDevice?.width ? (copyDevice.width / 10) : ''}
                    placeholder="напр., 17.5"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Высота (мм)</label>
                  <input
                    type="number"
                    name="height"
                    step="0.1"
                    min="0"
                    defaultValue={copyDevice?.height ? (copyDevice.height / 10) : ''}
                    placeholder="напр., 90"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Глубина (мм)</label>
                  <input
                    type="number"
                    name="depth"
                    step="0.1"
                    min="0"
                    defaultValue={copyDevice?.depth ? (copyDevice.depth / 10) : ''}
                    placeholder="напр., 71.5"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Визуальный шаблон</label>
                <input
                  type="text"
                  name="visual_template"
                  placeholder="SVG или идентификатор шаблона"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">URL документации</label>
                <input
                  type="url"
                  name="documentation_url"
                  defaultValue={copyDevice?.documentation_url || ''}
                  placeholder="https://example.com/docs/device"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
                {copyDevice?.documentation_url && (
                  <div className="mt-2">
                    <a
                      href={copyDevice.documentation_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                    >
                      📖 Открыть документацию
                      <span className="text-xs">(откроется в новом окне)</span>
                    </a>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={closeAllModals}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  disabled={createDeviceMutation.isPending}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                  {createDeviceMutation.isPending ? 'Создание...' : 'Создать устройство'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>

      {/* Edit Device Specification Modal */}
      <DeviceSpecEditModal
        isOpen={showEditForm}
        onClose={() => {
          setShowEditForm(false)
          setSelectedDevice(null)
        }}
        selectedDevice={selectedDevice}
        projectId={currentProjectId}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['specification', currentProjectId, currentPanelId] })
          setShowEditForm(false)
          setSelectedDevice(null)
        }}
      />

      {/* Contact Management Modal */}
      {showContactManager && selectedDevice && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setMouseDownTarget(e.target)
            }
          }}
          onMouseUp={(e) => {
            if (e.target === e.currentTarget && mouseDownTarget === e.target) {
              closeAllModals()
            }
            setMouseDownTarget(null)
          }}
        >
          <div 
            className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                ⚡ Контакты устройства "{selectedDevice.name}"
              </h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleOpenNewContactEditor}
                  className="bg-blue-600 text-white px-3 py-2 rounded text-sm hover:bg-blue-700"
                >
                  ➕ Добавить контакт
                </button>
                <button
                  onClick={closeAllModals}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="space-y-3">
              {contacts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p className="mb-3">Контакты не созданы</p>
                  <button
                    onClick={handleOpenNewContactEditor}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm"
                  >
                    Создать первый контакт
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          №
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Маркировка
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Цвет
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Показать
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Действия
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {contacts.map((contact) => (
                        <tr key={contact.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="text-sm font-medium text-blue-600">
                              #{contact.contact_number}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {contact.contact_label || `Контакт ${contact.contact_number}`}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {contact.color_code ? 
                                CONTACT_COLORS.find(c => c.value === contact.color_code)?.label || contact.color_code 
                                : 'Не указан'
                              }
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center">
                              <div 
                                className="w-6 h-4 rounded border border-gray-400 relative overflow-hidden"
                                style={{
                                  backgroundColor: (() => {
                                    const colorInfo = contact.color_code ? CONTACT_COLORS.find(c => c.value === contact.color_code) : null;
                                    return colorInfo?.color2 ? 'transparent' : (colorInfo?.color || (contact.color_code ? '#ccc' : 'transparent'));
                                  })(),
                                  backgroundImage: (() => {
                                    if (!contact.color_code) {
                                      return `linear-gradient(45deg, #ccc 25%, transparent 25%), 
                                              linear-gradient(-45deg, #ccc 25%, transparent 25%), 
                                              linear-gradient(45deg, transparent 75%, #ccc 75%), 
                                              linear-gradient(-45deg, transparent 75%, #ccc 75%)`;
                                    }
                                    const colorInfo = CONTACT_COLORS.find(c => c.value === contact.color_code);
                                    if (colorInfo?.color2) {
                                      return `linear-gradient(135deg, ${colorInfo.color} 0%, ${colorInfo.color} 47%, rgba(0,0,0,0.2) 49%, rgba(0,0,0,0.2) 51%, ${colorInfo.color2} 53%, ${colorInfo.color2} 100%)`;
                                    }
                                    return 'none';
                                  })(),
                                  backgroundSize: !contact.color_code ? '4px 4px' : '100% 100%',
                                  backgroundPosition: !contact.color_code ? '0 0, 0 2px, 2px -2px, -2px 0px' : 'center'
                                }}
                              />
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-right">
                            <div className="flex justify-end space-x-1">
                              <button
                                onClick={() => handleEditContact(contact)}
                                className="action-icon-btn edit"
                                title="Редактировать"
                              >
                                ✏️
                              </button>
                              <button
                                onClick={() => handleDeleteContact(contact)}
                                className="action-icon-btn delete"
                                title="Удалить"
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={closeAllModals}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contact Editor Modal */}
      {showContactEditor && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setMouseDownTarget(e.target)
            }
          }}
          onMouseUp={(e) => {
            if (e.target === e.currentTarget && mouseDownTarget === e.target) {
              closeContactEditor()
            }
            setMouseDownTarget(null)
          }}
        >
          <div 
            className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingContact ? 'Редактировать контакт' : 'Новый контакт'}
              </h3>
              <button
                onClick={closeContactEditor}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            {/* Quick Add Block - только при создании нового контакта */}
            {!editingContact && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Быстрое добавление</h4>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    onClick={handleQuickAddLN}
                    disabled={createContactMutation.isPending}
                    className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
                  >
                    L, N
                  </button>
                  <button
                    onClick={handleQuickAddLNPE}
                    disabled={createContactMutation.isPending}
                    className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 text-sm font-medium"
                  >
                    L, N, PE
                  </button>
                  <button
                    onClick={handleQuickAdd12}
                    disabled={createContactMutation.isPending}
                    className="px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 text-sm font-medium"
                  >
                    1, 2
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Номер контакта</label>
                <input
                  type="text"
                  value={newContact.contact_number}
                  onChange={(e) => setNewContact(prev => ({
                    ...prev,
                    contact_number: e.target.value
                  }))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleCreateContact()
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="1 или 1-TOP"
                />
              </div>



              <div>
                <label className="block text-sm font-medium mb-1">Маркировка</label>
                <input
                  type="text"
                  value={newContact.contact_label}
                  onChange={(e) => setNewContact(prev => ({
                    ...prev,
                    contact_label: e.target.value
                  }))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleCreateContact()
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Li, Lo, N, PE"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">На каких схемах отображать</label>
                {panelSchemasLoading ? (
                  <div className="text-sm text-gray-500 mt-2">Загрузка схем...</div>
                ) : allSchemas.length === 0 ? (
                  <div className="text-sm text-gray-500 mt-2">У проекта нет схем</div>
                ) : (
                  <div className="mt-2 space-y-4">
                    {allPanels.map(panel => {
                      // Фильтруем схемы для этого щита
                      const panelSchemas = allSchemas.filter(schema => schema.electrical_panel_id === panel.id)
                      
                      // Пропускаем щиты без схем
                      if (panelSchemas.length === 0) return null
                      
                      return (
                        <div key={panel.id} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                          <div className="text-sm font-semibold text-gray-700 mb-2">
                            Щит {panel.name}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {panelSchemas.map(schema => (
                              <button
                                key={schema.id}
                                type="button"
                                onClick={() => {
                                  setNewContact(prev => {
                                    const isSelected = prev.selected_schema_ids.includes(schema.id);
                                    const updatedIds = isSelected
                                      ? prev.selected_schema_ids.filter(id => id !== schema.id)
                                      : [...prev.selected_schema_ids, schema.id];
                                    
                                    return {
                                      ...prev,
                                      selected_schema_ids: updatedIds
                                    };
                                  });
                                }}
                                className={`flex items-center space-x-2 px-3 py-2 rounded-md border transition-colors ${
                                  newContact.selected_schema_ids.includes(schema.id)
                                    ? 'border-blue-500 bg-blue-50 text-blue-700' 
                                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 bg-white'
                                }`}
                              >
                                <span className="text-sm">📋</span>
                                <span className="text-sm font-medium">{schema.name}</span>
                                {newContact.selected_schema_ids.includes(schema.id) && (
                                  <span className="text-xs text-blue-600">✓</span>
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
                {newContact.selected_schema_ids.length > 0 && (
                  <div className="mt-3 text-sm text-gray-600">
                    Выбрано схем: {newContact.selected_schema_ids.length}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Расположение контакта</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {CONTACT_POSITIONS.map(position => (
                    <button
                      key={position.value}
                      type="button"
                      onClick={() => {
                        setNewContact(prev => ({
                          ...prev,
                          contact_position: position.value as 'top' | 'bottom' | 'left' | 'right'
                        }))
                      }}
                      className={`flex items-center space-x-2 px-3 py-2 rounded-md border transition-colors ${
                        newContact.contact_position === position.value 
                          ? 'border-blue-500 bg-blue-50 text-blue-700' 
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <span className="text-sm">{position.icon}</span>
                      <span className="text-sm font-medium">{position.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Цвет (необязательно)</label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {CONTACT_COLORS.map(color => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => {
                        setNewContact(prev => ({
                          ...prev,
                          color_code: color.value
                        }))
                      }}
                      className={`flex items-center space-x-2 p-2 rounded-md border transition-colors ${
                        newContact.color_code === color.value 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div
                        className="w-4 h-4 rounded border border-gray-400 flex-shrink-0 relative overflow-hidden"
                        style={{
                          backgroundColor: color.color2 ? 'transparent' : color.color,
                          backgroundImage: color.value === ''
                            ? `linear-gradient(45deg, #ccc 25%, transparent 25%),
                                linear-gradient(-45deg, #ccc 25%, transparent 25%),
                                linear-gradient(45deg, transparent 75%, #ccc 75%),
                                linear-gradient(-45deg, transparent 75%, #ccc 75%)`
                            : color.color2
                              ? `linear-gradient(135deg, ${color.color} 0%, ${color.color} 47%, rgba(0,0,0,0.2) 49%, rgba(0,0,0,0.2) 51%, ${color.color2} 53%, ${color.color2} 100%)`
                              : 'none',
                          backgroundSize: color.value === '' ? '4px 4px' : '100% 100%',
                          backgroundPosition: color.value === '' ? '0 0, 0 2px, 2px -2px, -2px 0px' : 'center'
                        }}
                      />
                      <span className="text-xs text-gray-700">{color.value ? color.label : 'Нет'}</span>
                    </button>
                  ))}
                </div>
                {newContact.color_code && (
                  <div className="mt-2 text-sm text-gray-600">
                    Выбран: <span className="font-medium">{CONTACT_COLORS.find(c => c.value === newContact.color_code)?.label || 'Не указан'}</span>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={closeContactEditor}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Отмена
                </button>
                <button
                  onClick={handleCreateContact}
                  disabled={createContactMutation.isPending || updateContactMutation.isPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {editingContact ? 
                    (updateContactMutation.isPending ? 'Сохранение...' : 'Сохранить') :
                    (createContactMutation.isPending ? 'Создание...' : 'Создать')
                  }
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Terminal Create Form Modal */}
      {showTerminalCreateForm && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setMouseDownTarget(e.target)
            }
          }}
          onMouseUp={(e) => {
            if (e.target === e.currentTarget && mouseDownTarget === e.target) {
              closeAllModals()
            }
            setMouseDownTarget(null)
          }}
        >
          <div 
            className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-4">🔗 Создать клеммный терминал</h3>
            <form onSubmit={handleCreateTerminal} className="space-y-4">
              {/* Hidden electrical_panel_id field */}
              {electricalPanelId && (
                <input 
                  type="hidden" 
                  name="electrical_panel_id" 
                  value={electricalPanelId} 
                />
              )}
              <div>
                <label className="block text-sm font-medium mb-1">Количество уровней *</label>
                <input
                  type="number"
                  name="contact_count"
                  min="1"
                  max="10"
                  defaultValue="1"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Например: 3 для 3-уровневого клеммника"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Указывайте количество уровней. Система автоматически создаст контакты (1U, 1D, 2U, 2D, и т.д.)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Название клеммника *</label>
                <input
                  type="text"
                  name="name"
                  required
                  placeholder="Например: ЗНИ-2.5"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Модель</label>
                <input
                  type="text"
                  name="model"
                  placeholder="Например: ЗНИ-2.5мм²"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Производитель</label>
                <input
                  type="text"
                  name="manufacturer"
                  placeholder="Например: ЭКФ"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>



              <div>
                <label className="block text-sm font-medium mb-1">Максимальный ток (А)</label>
                <input
                  type="number"
                  name="power_consumption"
                  step="0.1"
                  placeholder="Например: 24"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeAllModals}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  disabled={createTerminalMutation.isPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {createTerminalMutation.isPending ? 'Создание...' : 'Создать клеммник'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Terminal Edit Form Modal */}
      {showTerminalEditForm && editingTerminal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setMouseDownTarget(e.target)
            }
          }}
          onMouseUp={(e) => {
            if (e.target === e.currentTarget && mouseDownTarget === e.target) {
              closeAllModals()
            }
            setMouseDownTarget(null)
          }}
        >
          <div 
            className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-4">🔗 Редактировать клеммный терминал: {editingTerminal.name}</h3>
            <form onSubmit={handleEditTerminal} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Название клеммника *</label>
                <input
                  type="text"
                  name="name"
                  required
                  defaultValue={editingTerminal.name}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Модель</label>
                <input
                  type="text"
                  name="model"
                  defaultValue={editingTerminal.model || ''}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Производитель</label>
                <input
                  type="text"
                  name="manufacturer"
                  defaultValue={editingTerminal.manufacturer || ''}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Ширина (мм)</label>
                  <input
                    type="number"
                    name="width"
                    step="0.1"
                    min="0"
                    defaultValue={editingTerminal.width ? (editingTerminal.width / 10) : ''}
                    placeholder="напр., 17.5"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Высота (мм)</label>
                  <input
                    type="number"
                    name="height"
                    step="0.1"
                    min="0"
                    defaultValue={editingTerminal.height ? (editingTerminal.height / 10) : ''}
                    placeholder="напр., 90"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Глубина (мм)</label>
                  <input
                    type="number"
                    name="depth"
                    step="0.1"
                    min="0"
                    defaultValue={editingTerminal.depth ? (editingTerminal.depth / 10) : ''}
                    placeholder="напр., 71.5"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Максимальный ток (А)</label>
                <input
                  type="number"
                  name="power_consumption"
                  step="0.1"
                  defaultValue={editingTerminal.power_consumption || ''}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeAllModals}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  disabled={updateTerminalMutation.isPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {updateTerminalMutation.isPending ? 'Сохранение...' : 'Сохранить изменения'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Device Instance Creation Modal */}
      {showCreateInstanceForm && selectedDeviceForInstance && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setMouseDownTarget(e.target)
            }
          }}
          onMouseUp={(e) => {
            if (e.target === e.currentTarget && mouseDownTarget === e.target) {
              closeAllModals()
            }
            setMouseDownTarget(null)
          }}
        >
          <div 
            className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-4">Создать экземпляр устройства</h3>
            <form onSubmit={handleSubmitDeviceInstance} className="space-y-4">
              {/* Pre-filled fields */}
              <div>
                <label className="block text-sm font-medium mb-1">Спецификация</label>
                <input
                  type="text"
                  value={selectedDeviceForInstance.name}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Электрический щит</label>
                <input
                  type="text"
                  value={electricalPanelLoading ? 'Загрузка...' : electricalPanel ? `${electricalPanel.name} (${electricalPanel.proj_id})` : 'Не выбран'}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Позиционное обозначение *</label>
                <input
                  type="text"
                  name="position_label"
                  required
                  placeholder="Например: ДП-14.2-1, LD-1-1, УР-2-4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Укажите позиционное обозначение экземпляра устройства
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Описание (необязательно)</label>
                <textarea
                  name="description"
                  rows={3}
                  placeholder="Дополнительное описание экземпляра..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeAllModals}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  disabled={createDeviceInstanceMutation.isPending}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                  {createDeviceInstanceMutation.isPending ? 'Создание...' : 'Создать экземпляр'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
} 